// Core imports
import { Fragment, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { QuestionCircleOutlined } from '@ant-design/icons';

// Component imports
import { Table, Row, Col, Form, Empty, Space, Tag, Tooltip, Typography } from 'antd';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatoryFilters, useObservatorySearchParams, useObservatoryTenantsFilterState } from '../UI/ObservatoryFiltersRow';
import InteractionLogsSearchByInput from '../UI/InteractionLogsSearchByInput';
import InteractionLogDetailModal from '../UI/InteractionLogDetailModal';
import BotAvatar from '../UI/BotAvatar';

// Misc imports
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { useFetchInteractionsMetadataQuery } from '../../store/interactionMetadata/interactionMetadataApi';
import { INTERACTION_TYPE_FILTERS, INTERACTION_DURATION_FILTERS, DEFAULT_WEBCHAT_DEPLOYMENT } from '../../constants/main';
import { NAV_BOT } from '../../constants/navigation';
import replaceUrlParams from '../../utils/string/replaceUrlParams';

const PAGE_SIZE = 20;
const TABLE_SCROLL_X = 1020;
const { Paragraph, Text } = Typography;

const DURATION_FILTER_MAP = {
  short: { durationLte: 99 },
  regular: { durationGte: 100, durationLte: 1999 },
  long: { durationGte: 2000 }
};

const InteractionLogsTable = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [filtersForm] = Form.useForm();
  const [searchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || PAGE_SIZE, 10);
  const selectedInteractionId = searchParams.get('interactionId');

  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery(
    { orgId, urlParams: { includeBotDeployments: true } },
    { skip: !orgId }
  );

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const observatorySearchParams = useObservatorySearchParams(orgTimezone);
  const observatoryFilters = useObservatoryFilters(orgTimezone);
  const initialTenantFilters = useInitialTenantFilters();
  const { shouldSkipTenantsFilter } = useObservatoryTenantsFilterState(observatorySearchParams);

  const hasErrorFilters = useMemo(() => [
    { text: t('Yes'), value: 'true' },
    { text: t('No'), value: 'false' }
  ], [t]);

  const observatoryFiltersKey = useMemo(() => JSON.stringify(observatoryFilters || []), [observatoryFilters]);
  const prevObservatoryFiltersKeyRef = useRef(observatoryFiltersKey);

  const handleTableChange = useCallback(({
    pagination = { current: Number(searchParams.get('page')) || 1, pageSize: Number(searchParams.get('pageSize')) || pageSize },
    sorter = { field: searchParams.get('sortBy') || 'ts', order: searchParams.get('order')?.toLowerCase() === 'asc' ? 'ascend' : 'descend' },
    filters = {},
    shouldResetPagination = false
  } = {}) => {
    const urlSearchParams = new URLSearchParams(searchParams);

    urlSearchParams.set('page', shouldResetPagination ? 1 : pagination.current);
    urlSearchParams.set('pageSize', pagination.pageSize);

    const sortField = sorter.columnKey || sorter.field;
    if (sortField && sorter.order) {
      urlSearchParams.set('sortBy', sortField);
      urlSearchParams.set('order', sorter.order === 'ascend' ? 'ASC' : 'DESC');
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    if (filters.type && filters.type.length > 0) {
      urlSearchParams.set('types', filters.type.join(','));
    } else {
      urlSearchParams.delete('types');
    }

    if (filters.hasError && filters.hasError.length > 0) {
      urlSearchParams.set('hasError', filters.hasError[0]);
    } else {
      urlSearchParams.delete('hasError');
    }

    if (filters.duration_ms && filters.duration_ms.length > 0) {
      urlSearchParams.set('durationFilter', filters.duration_ms[0]);
    } else {
      urlSearchParams.delete('durationFilter');
    }

    navigate({ search: urlSearchParams.toString() }, { replace: true });
  }, [searchParams, pageSize, navigate]);

  useEffect(() => {
    if (prevObservatoryFiltersKeyRef.current !== observatoryFiltersKey) {
      prevObservatoryFiltersKeyRef.current = observatoryFiltersKey;
      handleTableChange({ shouldResetPagination: true });
    }
  }, [observatoryFiltersKey, handleTableChange]);

  const sortingInfo = useMemo(() => {
    const sortByParam = searchParams.get('sortBy');
    const orderParam = searchParams.get('order');
    if (!sortByParam || !orderParam) {
      return { columnKey: null, order: null };
    }
    return {
      columnKey: sortByParam,
      order: orderParam.toLowerCase() === 'asc' ? 'ascend' : 'descend'
    };
  }, [searchParams]);

  const durationFilterValue = searchParams.get('durationFilter');
  const durationParams = useMemo(
    () => durationFilterValue ? DURATION_FILTER_MAP[durationFilterValue] || {} : {},
    [durationFilterValue]
  );

  const queryUrlParams = useMemo(() => ({
    page: currentPage > 0 ? currentPage - 1 : 0,
    pageSize,
    sortBy: searchParams.get('sortBy') || 'ts',
    order: searchParams.get('order') || 'DESC',
    ...(observatorySearchParams.startDate && { startDate: observatorySearchParams.startDate }),
    ...(observatorySearchParams.endDate && { endDate: observatorySearchParams.endDate }),
    ...(observatorySearchParams.tenantsFilter && { tenantsFilter: observatorySearchParams.tenantsFilter }),
    ...(searchParams.get('types') && { types: searchParams.get('types').split(',') }),
    ...(searchParams.get('hasError') && { hasError: searchParams.get('hasError') }),
    ...(durationParams.durationGte !== undefined && { durationGte: durationParams.durationGte }),
    ...(durationParams.durationLte !== undefined && { durationLte: durationParams.durationLte }),
    ...(searchParams.get('searchQuery') && { [searchParams.get('searchBy') || 'sessionId']: decodeURIComponent(searchParams.get('searchQuery')) }),
    ...(orgTimezone && { timeZone: orgTimezone })
  }), [currentPage, pageSize, searchParams, observatorySearchParams, durationParams, orgTimezone]);

  const { data: interactionLogs, isFetching } = useFetchInteractionsMetadataQuery(
    { orgId, urlParams: queryUrlParams },
    { skip: !orgId || shouldSkipTenantsFilter || !observatorySearchParams.startDate || !observatorySearchParams.endDate || !organization }
  );

  const selectedInteraction = useMemo(() => {
    if (!selectedInteractionId || !interactionLogs?.items) {
      return null;
    }
    return interactionLogs.items.find(item => String(item.id) === selectedInteractionId);
  }, [selectedInteractionId, interactionLogs]);

  const resolveDeploymentName = useCallback((tenantId, deploymentId) => {
    if (!deploymentId) {
      return t('Unknown Deployment');
    }
    if (!bots?.items) {
      return deploymentId;
    }
    const bot = bots.items.find(b => b.id === tenantId);
    if (!bot || !bot.botDeployments) {
      return t('Unknown Deployment');
    }
    const deployment = bot.botDeployments.find(d => d.id === deploymentId);
    return deployment?.displayName || t('Unknown Deployment');
  }, [bots, t]);

  const handleRowClick = useCallback((record, e) => {
    if (e.target.closest('a')) {
      return;
    }
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.set('interactionId', record.id);
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  }, [searchParams, navigate]);

  const handleCloseSideModal = useCallback(() => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.delete('interactionId');
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  }, [searchParams, navigate]);

  const columns = useMemo(() => [
    {
      title: t('Agent'),
      dataIndex: 'tenantId',
      key: 'tenantId',
      width: 160,
      ellipsis: true,
      render: (tenantId) => {
        const bot = bots?.items?.find(b => b.id === tenantId);
        return <BotAvatar bot={bot} avatarSize={24} />;
      }
    },
    {
      title: t('Timestamp'),
      dataIndex: 'timestamp',
      key: 'ts',
      width: 180,
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'ts' && sortingInfo.order,
      defaultSortOrder: 'descend',
      render: (ts) => ts ? moment(ts).tz(orgTimezone).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      width: 120,
      filters: INTERACTION_TYPE_FILTERS,
      filteredValue: searchParams.get('types') ? searchParams.get('types').split(',') : null,
      filterMultiple: true,
      render: (type) => {
        const label = INTERACTION_TYPE_FILTERS.find(item => item.value === type)?.text || type;
        return <Tag>{label}</Tag>;
      }
    },
    {
      title: (
        <Space>
          {t('Name')}
          <Tooltip overlayClassName="pointer-events-none" title={(
            <Fragment>
              <Row><Col><Paragraph className="mb-1 text-inherit">{t('Displays the resolved name based on interaction type:')}</Paragraph></Col></Row>
              <Row><Col><Text className="text-inherit">{t('LLM / HTTP: Node name')}</Text></Col></Row>
              <Row><Col><Text className="text-inherit">{t('Variable / Search: Variable name')}</Text></Col></Row>
              <Row><Col><Text className="text-inherit">{t('Tags: N/A')}</Text></Col></Row>
            </Fragment>
          )}>
            <QuestionCircleOutlined tabIndex={0} role="button" aria-label={t('Name column info')} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'friendlyName',
      key: 'friendlyName',
      width: 200,
      ellipsis: true,
      render: (name) => name || '-'
    },
    {
      title: t('Deployment'),
      dataIndex: 'deploymentId',
      key: 'deploymentId',
      width: 160,
      ellipsis: true,
      render: (deploymentId, record) => {
        const name = resolveDeploymentName(record.tenantId, deploymentId);
        if (!deploymentId || !record.tenantId) {
          return name;
        }
        const bot = bots?.items?.find(b => b.id === record.tenantId);
        const deployment = bot?.botDeployments?.find(d => d.id === deploymentId);
        // thin DTO from includeBotDeployments lacks isPreview; match by name
        if (!deployment || deployment.displayName === DEFAULT_WEBCHAT_DEPLOYMENT.displayName) {
          return name;
        }
        const url = `${replaceUrlParams(NAV_BOT, { tenantId: record.tenantId, orgId })}?view=deployments&deployment=${deploymentId}`;
        return <Link to={url}>{name}</Link>;
      }
    },
    {
      title: t('Duration'),
      dataIndex: 'duration',
      key: 'duration_ms',
      width: 120,
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'duration_ms' && sortingInfo.order,
      filters: INTERACTION_DURATION_FILTERS,
      filteredValue: searchParams.get('durationFilter') ? [searchParams.get('durationFilter')] : null,
      filterMultiple: false,
      render: (duration) => duration !== null && duration !== undefined ? `${duration}ms` : '-'
    },
    {
      title: t('Errors'),
      dataIndex: 'hasError',
      key: 'hasError',
      width: 80,
      filters: hasErrorFilters,
      filteredValue: searchParams.get('hasError') ? [searchParams.get('hasError')] : null,
      filterMultiple: false,
      render: (hasError) => hasError ? <Tag color="error">{t('Yes')}</Tag> : <Tag color="success">{t('No')}</Tag>
    }
  ], [searchParams, resolveDeploymentName, t, orgTimezone, hasErrorFilters, sortingInfo, bots, orgId]);

  return (
    <Row gutter={[0, 0]} className="p-4">
      <Col span={24}>
        <Form
          form={filtersForm}
          initialValues={{
            tenantsFilter: initialTenantFilters,
            rangePicker: [observatoryFilters?.startDate, observatoryFilters?.endDate]
          }}
        >
          <ObservatoryFiltersRow
            form={filtersForm}
            rangePickerProps={{ disabled: isFetching, timezone: orgTimezone }}
            agentsPickerProps={{
              agentsList: bots?.items || [],
              disabled: isFetching,
              loading: isFetchingBots
            }}
          />
        </Form>
      </Col>

      <Col flex="auto" className="max-field-width mb-2">
        <InteractionLogsSearchByInput />
      </Col>

      <Col span={24}>
        <Table
          rowKey="id"
          loading={isFetching || isFetchingBots}
          dataSource={interactionLogs?.items || []}
          columns={columns}
          rowClassName="cursor-pointer"
          onChange={(pagination, filters, sorter) => handleTableChange({ pagination, filters, sorter })}
          onRow={(record) => ({
            onClick: (e) => handleRowClick(record, e)
          })}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: interactionLogs?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `${t('Total')}: ${total}`
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('No interaction logs found for the selected filters.')}
              />
            )
          }}
          scroll={{ x: TABLE_SCROLL_X }}
        />
      </Col>

      <InteractionLogDetailModal
        interaction={selectedInteraction}
        open={!!selectedInteraction}
        onClose={handleCloseSideModal}
        timezone={orgTimezone}
      />
    </Row>
  );
};

export default InteractionLogsTable;
